import { translateCurrent } from './i18n';
const STORAGE_KEY = 'doddledays:adaptive-connection';
const OFFLINE_MODE_KEY = 'doddledays:offline-mode';
const NETWORK_EVENT = 'doddledays:network-quality';
const MAX_SAMPLES = 6;

export type NetworkQuality = 'good' | 'degraded' | 'poor' | 'offline';

let samples: number[] = [];
let consecutiveFailures = 0;
let quality: NetworkQuality = 'good';
let nativeOnlineReader: (() => boolean) | null = null;

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

function friendlyNetworkError(message: string): TypeError {
  const error = new TypeError(message);
  // Preserve TypeError for offline fallback detection, but do not leak a technical prefix when
  // callers stringify the error for user-facing status text.
  error.name = '';
  return error;
}

export function getAdaptiveConnectionEnabled(): boolean {
  if (!hasWindow()) return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setAdaptiveConnectionEnabled(enabled: boolean): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // A storage failure should never make network access fail.
  }
}

export function getOfflineModeEnabled(): boolean {
  if (!hasWindow()) return false;
  try {
    return window.localStorage.getItem(OFFLINE_MODE_KEY) === 'on';
  } catch {
    return false;
  }
}

function installNavigatorOfflineOverlay(): void {
  if (!hasWindow() || typeof navigator === 'undefined') return;
  try {
    if (!nativeOnlineReader) {
      const prototype = typeof Navigator !== 'undefined' ? Navigator.prototype : Object.getPrototypeOf(navigator);
      const descriptor = prototype ? Object.getOwnPropertyDescriptor(prototype, 'onLine') : undefined;
      const nativeGetter = descriptor?.get;
      if (nativeGetter) nativeOnlineReader = () => Boolean(nativeGetter.call(navigator));
      else {
        const initialOnline = navigator.onLine !== false;
        nativeOnlineReader = () => initialOnline;
      }
    }
    Object.defineProperty(navigator, 'onLine', {
      configurable: true,
      get: () => getOfflineModeEnabled() ? false : (nativeOnlineReader?.() ?? true),
    });
  } catch {
    // Network blocking still works if this browser does not allow an onLine overlay.
  }
}

export function setOfflineModeEnabled(enabled: boolean): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(OFFLINE_MODE_KEY, enabled ? 'on' : 'off');
  } catch {
    // Keep the in-memory app usable even if the browser refuses preference storage.
  }
  installNavigatorOfflineOverlay();
  publishQuality(true);
}

function connectionHint(): NetworkQuality | null {
  if (getOfflineModeEnabled()) return 'offline';
  if (typeof navigator === 'undefined') return null;
  if (navigator.onLine === false) return 'offline';
  const connection = (navigator as Navigator & {
    connection?: { effectiveType?: string; rtt?: number; downlink?: number; saveData?: boolean };
  }).connection;
  if (!connection) return null;
  if (connection.saveData || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') return 'poor';
  if (connection.effectiveType === '3g' || (connection.rtt ?? 0) >= 900 || (connection.downlink ?? Infinity) < 1) return 'degraded';
  return null;
}

function classify(): NetworkQuality {
  const hint = connectionHint();
  if (hint === 'offline') return 'offline';
  if (consecutiveFailures >= 2) return 'poor';
  if (samples.length === 0) return hint ?? 'good';
  const sorted = [...samples].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
  if (median >= 2200 || hint === 'poor') return 'poor';
  if (median >= 900 || hint === 'degraded') return 'degraded';
  return 'good';
}

function publishQuality(force = false): void {
  const next = classify();
  if (!force && next === quality) return;
  quality = next;
  if (hasWindow() && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NETWORK_EVENT, { detail: quality }));
  }
}

function recordSuccess(elapsedMs: number): void {
  samples = [...samples.slice(-(MAX_SAMPLES - 1)), elapsedMs];
  consecutiveFailures = 0;
  publishQuality();
}

function recordFailure(): void {
  consecutiveFailures += 1;
  publishQuality();
}

export function getNetworkQuality(): NetworkQuality {
  quality = classify();
  return quality;
}

export function subscribeNetworkQuality(listener: (next: NetworkQuality) => void): () => void {
  if (!hasWindow()) return () => undefined;
  const handler = (event: Event) => listener((event as CustomEvent<NetworkQuality>).detail);
  window.addEventListener(NETWORK_EVENT, handler);
  return () => window.removeEventListener(NETWORK_EVENT, handler);
}

function readTimeoutMs(current: NetworkQuality): number {
  if (current === 'poor') return 2800;
  if (current === 'degraded') return 5000;
  return 9000;
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

/**
 * Fetch wrapper used by Supabase. Reads fail fast when the measured connection is poor so
 * Doddledays' existing offline snapshots can take over instead of leaving the UI waiting.
 * Mutations are never given the aggressive timeout because aborting a write after the server
 * accepted it could make the client retry an already-applied change.
 */
export async function adaptiveFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (getOfflineModeEnabled()) {
    publishQuality();
    throw friendlyNetworkError(translateCurrent('connection.offlineOnly'));
  }
  if (!getAdaptiveConnectionEnabled()) return fetch(input, init);
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    recordFailure();
    throw friendlyNetworkError(translateCurrent('connection.deviceOffline'));
  }

  const method = requestMethod(input, init);
  const isRead = method === 'GET' || method === 'HEAD';
  const controller = isRead ? new AbortController() : null;
  const timeout = controller ? window.setTimeout(() => controller.abort(), readTimeoutMs(getNetworkQuality())) : null;
  const upstreamSignal = init?.signal;
  const abortFromUpstream = () => controller?.abort(upstreamSignal?.reason);
  if (controller && upstreamSignal) {
    if (upstreamSignal.aborted) controller.abort(upstreamSignal.reason);
    else upstreamSignal.addEventListener('abort', abortFromUpstream, { once: true });
  }

  const started = performance.now();
  try {
    const response = await fetch(input, controller ? { ...init, signal: controller.signal } : init);
    recordSuccess(performance.now() - started);
    return response;
  } catch (error) {
    recordFailure();
    if (controller?.signal.aborted && !upstreamSignal?.aborted) {
      throw friendlyNetworkError(translateCurrent('connection.slowFallback'));
    }
    throw error;
  } finally {
    if (timeout !== null) window.clearTimeout(timeout);
    if (controller && upstreamSignal) upstreamSignal.removeEventListener('abort', abortFromUpstream);
  }
}

installNavigatorOfflineOverlay();
